import { Link } from "react-router-dom";
import { Section, Step, Callout, KeyTerm, TOC, Pill } from "./HelpComponents";

const TOC_ITEMS = [
  { id: "what",        title: "What ArmIQ is" },
  { id: "join",        title: "Joining your team" },
  { id: "first-login", title: "First sign-in" },
  { id: "biometrics",  title: "Connect Apple Watch / Pixel" },
  { id: "daily",       title: "Your daily log (30 sec)" },
  { id: "hybrid",      title: "Your suggested hybrid" },
  { id: "routine",     title: "Pre-throwing routine" },
  { id: "history",     title: "Your history view" },
  { id: "scores",      title: "What the scores mean" },
  { id: "honesty",     title: "Be honest — it matters" },
];

export default function PlayerGuide() {
  return (
    <div className="grid lg:grid-cols-[1fr_280px] gap-10" data-testid="player-guide">
      <article className="space-y-12 max-w-3xl">
        <header>
          <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Athlete Handbook · v2.7</div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter text-white mt-2">How to crush ArmIQ as an athlete</h1>
          <p className="text-base text-white/60 mt-3 max-w-2xl">
            ArmIQ is your daily check-in tool. 30 seconds in the morning, an Apple Watch on your wrist,
            and the system tells you exactly which throwing track to run. This guide takes 5 minutes.
          </p>
        </header>

        <Section id="what" kicker="The 1-minute version" title="What ArmIQ actually is">
          <p>
            ArmIQ is the dashboard your coaches use to plan your throwing — but most of the data comes
            from <strong>you</strong>. Three inputs power everything:
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            <KeyTerm term="Your daily log">
              4 slider ratings + which hybrid you're running today. Takes 30 seconds.
            </KeyTerm>
            <KeyTerm term="Your Apple Watch / Pixel">
              HRV, sleep, resting HR, workouts. Synced automatically once a day.
            </KeyTerm>
            <KeyTerm term="Your coach's measurements">
              Baseline (ROM, strength, jumps) once a year. FlightScope/Rapsodo when you throw.
            </KeyTerm>
          </div>
          <p>
            From those, ArmIQ computes a <strong>Readiness Score (0–100)</strong> every morning and tells
            you which Hybrid (A/B/C/D/E) to run. Your coach sees the same numbers — that means fewer "why
            am I throwing today?" arguments.
          </p>
        </Section>

        <Section id="join" kicker="Step 1" title="Joining your team">
          <p>
            Your coach gives you one of two things. <strong>Most likely</strong>, it's an invite link
            (looks like <code className="text-white">https://armiq.app/invite/abc123…</code>).
          </p>
          <Step n={1} title="Open the invite link">
            On your phone. The page already knows your name and the team. Pick the email + password
            you want to use to sign in (the email will be how you reset your password later).
          </Step>
          <Step n={2} title="Hit Create account">
            ArmIQ signs you in automatically and lands you on the player portal at
            <code className="text-white"> /portal</code>.
          </Step>
          <Callout kind="info" title="If your coach handed you a password instead">
            Go to <code className="text-white">/login</code>, type the email and password they shared,
            sign in. Optional: open Settings later and change the password to something memorable.
          </Callout>
        </Section>

        <Section id="first-login" kicker="Step 2" title="What you see on first sign-in">
          <p>The portal has 3 strips, top to bottom:</p>
          <Step n={1} title="Your Next Track card">
            Big hybrid letter, intensity, and a one-line note like "Long Toss with Pull-Downs".
            Underneath: a colored <strong>suggested-hybrid chip</strong> + your Readiness Score.
          </Step>
          <Step n={2} title="Shortcut buttons">
            <em>Pre-Throwing Routine</em> · <em>History</em> · <em>Apple Health</em>. One tap each.
          </Step>
          <Step n={3} title="Today's daily log">
            Four sliders + hybrid picker + throwing environment + (optional) pitch counts. Drag, tap
            <em> Submit</em>, you're done.
          </Step>
        </Section>

        <Section id="biometrics" kicker="Step 3 — do this ONCE" title="Connect your Apple Watch or Pixel">
          <p>
            Without this, your Recovery and Sleep sub-scores stay at the default 60%. You want the
            real numbers feeding in.
          </p>
          <Step n={1} title="Open Biometrics on the portal">
            Tap the <strong>Apple Health</strong> shortcut at the top of <code className="text-white">/portal</code>.
            That's the page at <code className="text-white">/portal/apple-health</code>.
          </Step>
          <Step n={2} title="Copy your unique sync token">
            The page shows a long string starting with <code>ahk_…</code>. Tap <em>Copy</em>.
          </Step>
          <Step n={3} title="Install the ArmIQ companion app">
            <strong>iPhone</strong>: ArmIQ from the App Store (or TestFlight link your coach sends).
            <strong> Android (Pixel / Galaxy)</strong>: ArmIQ from the Play Store. You also need the
            Google <em>Health Connect</em> app installed (Pixel 6+ and Galaxy S22+ ship with it).
          </Step>
          <Step n={4} title="Paste the token, hit Sync">
            On first launch, paste the token, grant HealthKit / Health Connect permissions, tap
            <em> Sync today's metrics</em>. From now on, the app syncs automatically once per day.
          </Step>
          <Callout kind="good" title="What we read">
            HRV (Heart Rate Variability), resting heart rate, sleep duration, VO₂ Max, workouts.
            We <strong>never write</strong> anything back to your Health app. Your raw data stays on
            your phone — only daily summaries hit ArmIQ.
          </Callout>
        </Section>

        <Section id="daily" kicker="Every morning" title="Your daily log — the 30-second routine">
          <p>Five inputs (your coach can add or remove some). Slide each from 1 to 5:</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <KeyTerm term="Arm Health">1 = severe pain, 3 = OK, 5 = 100% fresh.</KeyTerm>
            <KeyTerm term="Body Soreness">1 = wrecked, 5 = fully recovered.</KeyTerm>
            <KeyTerm term="Sleep Quality">1 = restless &lt;6h, 5 = restorative &gt;8h.</KeyTerm>
            <KeyTerm term="Mental Energy">1 = drained, 5 = peak competitive drive.</KeyTerm>
          </div>
          <p>Then:</p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li><strong>Today's hybrid track</strong> — pre-selected to ArmIQ's suggestion. Change only if your coach told you to.</li>
            <li><strong>Throwing environment</strong> — Flat Ground / Long Toss / Bullpen / Live BP / Game / Rest.</li>
            <li><strong>Max distance, pitch count, 75% accuracy %</strong> — fill in after the session.</li>
            <li><strong>Pain DURING session</strong> — toggle ON only if you felt actual pain. This fires a CRITICAL alert to your coach immediately.</li>
            <li><strong>Post-throw fatigue</strong> — 1 (no fatigue) to 5 (exhausted).</li>
          </ul>
          <Callout kind="warn" title="The 'Pain DURING' toggle is serious">
            Flipping that ON sends an instant alert to the coaching staff and locks your status to
            CRITICAL. Don't toggle it casually — but don't be a hero either. If it hurt, it hurt.
          </Callout>
        </Section>

        <Section id="hybrid" kicker="The closed loop" title="Your suggested hybrid">
          <p>
            Every morning, ArmIQ looks at your readiness data and picks one of four tracks for you:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-lg ink-card p-4 text-center">
              <div className="font-display font-black text-3xl" style={{ color: "#D90429" }}>A</div>
              <div className="text-xs text-white/60 mt-1">You're fully recovered. Game-day prep — long toss + pull-downs.</div>
            </div>
            <div className="rounded-lg ink-card p-4 text-center">
              <div className="font-display font-black text-3xl" style={{ color: "#F59E0B" }}>B</div>
              <div className="text-xs text-white/60 mt-1">Solid day. Long toss, no pull-downs.</div>
            </div>
            <div className="rounded-lg ink-card p-4 text-center">
              <div className="font-display font-black text-3xl" style={{ color: "#22D3EE" }}>C</div>
              <div className="text-xs text-white/60 mt-1">Something's off. Range-of-motion + light toss.</div>
            </div>
            <div className="rounded-lg ink-card p-4 text-center">
              <div className="font-display font-black text-3xl" style={{ color: "#16A34A" }}>D</div>
              <div className="text-xs text-white/60 mt-1">Recovery day. ROM throws only — no real throws.</div>
            </div>
          </div>
          <Callout kind="info" title="Don't fight the suggestion">
            If you got a D and you feel fine, trust the data — your HRV or workload says otherwise.
            Pushing through a D day is how guys end up on the IL.
          </Callout>
        </Section>

        <Section id="routine" kicker="Before every session" title="The Pre-Throwing Routine">
          <p>
            Tap the <strong>Pre-Throwing Routine</strong> shortcut (or go to <code className="text-white">/portal/routine</code>).
            Same routine for every hybrid — 4 areas, top to bottom:
          </p>
          <ol className="list-decimal list-inside ml-2 space-y-1">
            <li><strong>BODY</strong> — 1 lap, pick 3 (ladders / cones / hurdles / sprints), tissue work.</li>
            <li><strong>MOBILITY</strong> — hip work, squats, lunges, hip flow circuit, 5-move flow.</li>
            <li><strong>ARM ACTIVATION</strong> — Walking T's, arm circles, J-Bands, drop catch, SLDL, med ball, body blade, lax ball.</li>
            <li><strong>PLYOCARE THROWS</strong> — reverse throws, pivot pickoffs, roll-ins, walking windups, rocker.</li>
          </ol>
          <p>
            Each block with a <Pill color="#D90429">Video</Pill> tag has an embedded clip — tap to
            watch the form before you run it. <strong>7 reference videos</strong> total cover the moves
            you'll see most often.
          </p>
        </Section>

        <Section id="history" kicker="Track your own trend" title="Your history view">
          <p>
            Tap the <strong>History</strong> shortcut → <code className="text-white">/portal/history</code>.
            Three sections:
          </p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li><strong>30-day workload area chart</strong> — your total throws + workout minutes per day.</li>
            <li><strong>Readiness trend chart</strong> — Arm / Sleep / Soreness / Fatigue overlaid as lines.</li>
            <li><strong>Recent logs table</strong> — every submission with its computed status.</li>
          </ul>
          <Callout kind="info" title="Use the trend, not the day">
            One bad sleep night doesn't mean anything. A 5-day downward trend in HRV + sleep is the
            shape that predicts injury. The chart is there so you spot it before your coach does.
          </Callout>
        </Section>

        <Section id="scores" kicker="What the colors mean" title="Reading your Readiness Score">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-lg ink-card p-4">
              <div className="flex items-center justify-between"><span className="font-display font-bold text-white">PEAK</span><Pill color="#16A34A">≥ 80</Pill></div>
              <div className="text-sm text-white/60 mt-1">Fully loaded. Attack the day.</div>
            </div>
            <div className="rounded-lg ink-card p-4">
              <div className="flex items-center justify-between"><span className="font-display font-bold text-white">GOOD</span><Pill color="#22D3EE">65–79</Pill></div>
              <div className="text-sm text-white/60 mt-1">Solid. Default to your prescribed hybrid.</div>
            </div>
            <div className="rounded-lg ink-card p-4">
              <div className="flex items-center justify-between"><span className="font-display font-bold text-white">CAUTION</span><Pill color="#F59E0B">45–64</Pill></div>
              <div className="text-sm text-white/60 mt-1">Something's off. Check sleep + HRV first.</div>
            </div>
            <div className="rounded-lg ink-card p-4">
              <div className="flex items-center justify-between"><span className="font-display font-bold text-white">RISK</span><Pill color="#EF4444">&lt; 45</Pill></div>
              <div className="text-sm text-white/60 mt-1">Mandatory recovery. No real throws today.</div>
            </div>
          </div>
          <p>
            Score = weighted sum of 4 sub-scores (Recovery, Sleep, Load, Subjective). If you see a
            yellow <strong>HRV Auto-Hold</strong> chip on your portal, the system flagged a 14-day
            HRV drop and is forcing a recovery day regardless of what your sliders say.
          </p>
        </Section>

        <Section id="honesty" kicker="The contract" title="Be honest — it matters">
          <p>
            ArmIQ is a tool, not a judge. If you sandbag (lie low to skip throwing) or pump (lie high to
            stay in rotation), the data lies too, and your coach can't help you. The whole point is to
            replace guessing with measuring.
          </p>
          <Callout kind="good" title="The deal">
            Be honest on the sliders. Wear your watch. Trust the suggestion. ArmIQ keeps you healthier
            and throwing harder — and your coach off your back about "how do you actually feel?"
          </Callout>
        </Section>

        <footer className="pt-6 border-t border-[var(--ink-border)] text-xs text-white/40">
          Questions? Ask your coach in person — or, if you're stuck on the portal, send feedback through
          your <Link to="/portal" className="underline">portal</Link> footer.
        </footer>
      </article>

      <aside className="hidden lg:block">
        <TOC items={TOC_ITEMS} />
      </aside>
    </div>
  );
}
